import { InjectionToken, Injectable, OnDestroy, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Observable, Subject } from 'rxjs';

import { io, Socket } from 'socket.io-client';

/**
 * Injectable indirection around socket.io-client's `io` factory. Letting
 * tests override this via Angular DI (rather than `vi.mock`'ing the
 * socket.io-client package directly) avoids a real flakiness hazard: Vite
 * pre-bundles third-party CJS/ESM-interop packages like socket.io-client for
 * performance, and that pre-bundling step sits outside Vitest's per-file
 * mock interception — under parallel test execution, `vi.mock` can
 * intermittently lose the race and the real client gets used instead.
 */
export const IO_FACTORY = new InjectionToken<typeof io>('IO_FACTORY', {
  providedIn: 'root',
  factory: () => io,
});

@Injectable({
  providedIn: 'root',
})
export class WebsocketService implements OnDestroy {
  private socket!: Socket;
  private destroy$ = new Subject<void>();

  private reconnectSubject = new Subject<boolean>();
  private currentUserId?: number;

  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly ioFactory = inject(IO_FACTORY);

  connect(userId?: number): void {
    if (!this.isBrowser) {
      return;
    }

    if (userId) {
      this.currentUserId = userId;
    }

    if (this.socket?.connected) {
      return;
    }

    this.socket = this.ioFactory(window.location.origin, {
      transports: ['websocket'],
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
      // Required for the session cookie to reach the handshake — the server
      // now authenticates sockets via the same session, and rejects any
      // connection that doesn't carry it.
      withCredentials: true,
    });

    this.socket.on('connect', () => {
      console.log('Socket.io connected', this.socket?.id, 'recovered:', this.socket?.recovered);

      if (this.currentUserId) {
        this.identify(this.currentUserId);
      }

      // If the server has connectionStateRecovery enabled and this reconnect
      // landed within its window, Socket.io already restored our room
      // memberships and replayed anything we missed straight into our
      // existing listeners before this fires — callers can skip redoing
      // that work themselves.
      this.reconnectSubject.next(this.socket?.recovered ?? false);
    });

    this.socket.on('disconnect', (reason) => {
      console.log('Socket.io disconnected', reason);
    });

    this.socket.on('connect_error', (err) => {
      console.log('Connection Error', err.message);
    });
  }

  emit(event: string, payload: any): void {
    if (!this.socket) {
      console.warn('Socket does not exist');
      return;
    }
    if (this.socket.connected) {
      this.socket.emit(event, payload);
      return;
    }

    this.socket.once('connect', () => {
      this.socket?.emit(event, payload);
    });
  }

  on<T>(event: string): Observable<T> {
    return new Observable((observer) => {
      const handler = (data: T) => {
        observer.next(data);
      };

      if (this.socket) {
        this.socket.on(event, handler);
      } else {
        console.warn(`Socket not established for event: ${event}`);
      }

      return () => {
        if (this.socket) {
          this.socket.off(event, handler);
        }
      };
    });
  }

  identify(userId: number): void {
    this.currentUserId = userId;

    if (this.socket?.connected) {
      this.socket.emit('user:identify', userId);
    }
  }

  /**
   * Emits on every (re)connect with whether Socket.io recovered the prior
   * connection state (rooms rejoined and missed events replayed
   * automatically) — false for a first-ever connect, a recovery window
   * that expired, or a server restart during the gap.
   */
  onReconnect(): Observable<boolean> {
    return this.reconnectSubject.asObservable();
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.disconnect();
  }
}
